/**
 * KAN-884: layout of the conversation panel by screen width.
 *
 * Wide screen: the header (title row + client block) above the chat, as before.
 * Narrow screen (NARROW_SCREEN_QUERY, max-width 767px) with a client: the
 * title row stays on top, below it two tabs: "Переписка" (default: feed and
 * input, the feed takes the free height) and "Клиент" (the whole client block
 * with its own scroll). The inactive tab is hidden, not unmounted.
 *
 * One stable tree for every width and tab: both panels are always mounted
 * (forceMount), so the input draft (text, photo) survives a tab switch and a
 * rotation across 767px. On a wide screen the tab list is not rendered and
 * both panels are shown, the client block above the chat.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { NARROW_SCREEN_QUERY } from '@/hooks/useCrmBoardView'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

type ConversationTab = 'chat' | 'client'

export interface ConversationPhoneTabsProps {
  /** Always on top: back button and the title row with the client link. */
  titleRow: ReactNode
  /** Client block (info, next steps, controls, share/copy, our account). */
  clientBlock: ReactNode
  /** Tabs on a narrow screen (only with a client: without one the block is tiny). */
  withTabs: boolean
  /** Feed, account picker and input. */
  chat: ReactNode
  /** Classes of the header block (the modal leaves room for its close cross). */
  headerClassName?: string
  /** Called when the chat becomes visible (tab back, width change): scroll the feed. */
  onChatShow?: () => void
}

const headerClass = 'flex shrink-0 flex-col space-y-1.5 text-left'

export function ConversationPhoneTabs({
  titleRow,
  clientBlock,
  withTabs,
  chat,
  headerClassName,
  onChatShow,
}: ConversationPhoneTabsProps) {
  const narrow = useMediaQuery(NARROW_SCREEN_QUERY)
  const [tab, setTab] = useState<ConversationTab>('chat')
  const tabbed = narrow && withTabs
  const chatHidden = tabbed && tab !== 'chat'
  const clientHidden = tabbed && tab !== 'client'

  // A hidden feed loses its scroll position: put it back when shown again.
  useEffect(() => {
    if (!chatHidden) onChatShow?.()
  }, [chatHidden, onChatShow])

  return (
    <>
      <div className={cn(headerClass, headerClassName)}>{titleRow}</div>
      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as ConversationTab)}
        // Wide: the client block sits right under the title, as in one header.
        className={cn('flex min-h-0 flex-1 flex-col gap-3', !tabbed && '-mt-1.5')}
      >
        {tabbed && (
          <TabsList className="grid w-full shrink-0 grid-cols-2">
            <TabsTrigger value="chat">Переписка</TabsTrigger>
            <TabsTrigger value="client">Клиент</TabsTrigger>
          </TabsList>
        )}
        <TabsContent
          value="client"
          forceMount
          hidden={clientHidden}
          tabIndex={tabbed ? 0 : -1}
          className={cn(
            'mt-0 space-y-1.5',
            clientHidden
              ? 'hidden'
              : tabbed
                ? 'min-h-0 flex-1 overflow-y-auto'
                : 'flex shrink-0 flex-col text-left',
          )}
        >
          {clientBlock}
        </TabsContent>
        <TabsContent
          value="chat"
          forceMount
          hidden={chatHidden}
          tabIndex={tabbed ? 0 : -1}
          className={cn('mt-0', chatHidden ? 'hidden' : 'flex min-h-0 flex-1 flex-col gap-3')}
        >
          {chat}
        </TabsContent>
      </Tabs>
    </>
  )
}
